import { useEffect } from 'react';
import { css } from '@emotion/react';
import { DemoDataBanner, GeneralFeedbackButton } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { URLs } from '../../../../constants';
import { featureFlags } from '../../../../feature-flags';
import { customROITimePeriods } from '../../constants';
import { getDateRangeText } from '../../utils/date-range-text';
import { Filters } from '../components/filters';
import { NSRRoiDashboardMulti } from '../components/nsr-roi-dashboard-multi';
import { NSRRoiDashboardSingle } from '../components/nsr-roi-dashboard-single';
import { NSRSummary } from '../components/nsr-summary';
import { StatusMappingBanner } from '../components/status-mapping-banner';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';

export const NSRRoiDashboard = () => {
  const { t } = useTranslation('analytics');
  const {
    filterHintText: subtitle,
    filters,
    setDefinitionsHelperDateRange,
  } = useNSRRoiShallowStore('filterHintText', 'filters', 'setDefinitionsHelperDateRange');

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);
  const showNSRAccuracyBanner = getFeatureFlagValue(featureFlags.showNSRAccuracyBanner);

  useEffect(() => {
    setDefinitionsHelperDateRange(
      getDateRangeText({
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        timePeriods: customROITimePeriods,
      })
    );
  }, [filters?.startDate, filters?.endDate, setDefinitionsHelperDateRange]);

  return (
    <>
      {isDemoAccount ? (
        <DemoDataBanner title={t("You're taking No-Show Rate Conversion for a test drive with demo data!")} />
      ) : (
        showNSRAccuracyBanner && <StatusMappingBanner />
      )}
      <Page>
        <Page.Header>
          <Page.Header.Breadcrumbs
            breadcrumbs={[
              {
                label: t('Analytics'),
                to: URLs.ANALYTICS_DASHBOARD,
              },
              {
                label: t('No-Show Rate'),
              },
            ]}
          />
          <Page.Header.Heading>
            <Page.Header.Title title={t('No-Show Rate')} />
            <Page.Header.Subtitle subtitle={t('Showing results for {{subtitle}}', { subtitle })} />
          </Page.Header.Heading>
          <Page.Header.Action>
            <GeneralFeedbackButton selectedLocationFromPageFilter={filters?.locationIds} />
          </Page.Header.Action>
        </Page.Header>

        <Page.Body css={styles.body}>
          <div css={styles.body}>
            <Filters />
            <NSRSummary />
            {(filters?.locationIds?.length || 0) > 1 ? <NSRRoiDashboardMulti /> : <NSRRoiDashboardSingle />}
          </div>
        </Page.Body>
      </Page>
    </>
  );
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
};
